import SDK, { MFChannel } from '../../../../SDK';
import Translate from './Translate';
import React, { Fragment, useEffect, useState } from 'react';
import Store from '../../../../../Store';
import { useSelector } from 'react-redux';
import { showModal } from '../../../../../Actions/PopUp';
import { callIntermediateScreen } from '../../../../../Actions/CallAction';
import { MeetingLinkIcon } from '../../../../../assets/images';
import { CALL_STATUS_CONNECTED, CALL_STATUS_RECONNECT } from '../../../../../Helpers/Call/Constant';
import {
    blockOfflineAction,
    convertTextToURL,
    escapeHtml,
    getFormattedText,
    getSiteDomain,
    isCallLink,
    isGroupInviteLink,
} from '../../../../../Helpers/Utility';
import { getDataFromRoster, handleMentionedUser } from '../../../../../Helpers/Chat/User';
import PropTypes from 'prop-types';
import { resetActionModal, setActionModal } from '../../../../../channels/utils';
import JoinRequest from '../../../../../channels/conversation/ChannelMessage/components/JoinRequest';
import GroupJoinRequest from '../../../../../channels/conversation/ChannelMessage/components/GroupJoinRequest';
import { REACT_APP_SITE_DOMAIN } from '../../../../processENV';
import { useMessageProcessing } from './useMessageProcessing';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ActiveChatAction } from '../../../../../Actions/RecentChatActions';
import { CHAT_TYPE_SINGLE } from '../../../../../Helpers/Chat/Constant';

const HtmlToReactParser = require('html-to-react').Parser;
const htmlToReactParser = new HtmlToReactParser();

export default function TextComponent(props = {}) {
    const dispatch = useDispatch();

    const vCardData = useSelector(state => state.vCardData);
    const { data: conferenceData = {} } = useSelector(state => state.showConfrenceData || {});
    const { data: callData = {} } = useSelector(state => state.callIntermediateScreen || {});
    const { data: recentChatData } = useSelector(state => state.recentChatData || {});

    const { messageObject = {}, isSender = false, pageType } = props;
    const { msgBody = {}, editedStatus = 0 } = messageObject;
    const { data = {} } = vCardData;
    const messageLink = convertTextToURL(msgBody.message);

    const [callLinkMessageBody, setCallLinkMessageBody] = useState({
        prevMeetText: null,
        meetLink: null,
        endMeetText: null,
        prevtextMentionuser: null,
        endtextMentionuser: null,
        editedMeetLink: null,
    });

    // Hook for real-time validation of message rendering
    const { messageRef } = useMessageProcessing({
        messageObject,
        isSender,
        vCardData,
        htmlToReactParser,
    });

    const handleMentionedUserClick = useCallback(
        e => {
            // Check if clicked element or its parent is a mentioned user
            const mentionedElement =
                e.target.closest('.mentioned') || (e.target.classList.contains('mentioned') ? e.target : null);

            if (mentionedElement) {
                e.preventDefault();
                e.stopPropagation();

                const userId = mentionedElement.getAttribute('data-mentioned');

                if (userId && userId !== vCardData.data.userId) {
                    const data = getDataFromRoster(userId);
                    const chatData = recentChatData?.find(chatdata => chatdata.fromUserId === userId);
                    const activeChatData = {
                        recent: chatData || {
                            chatType: CHAT_TYPE_SINGLE,
                        },
                        roster: data || {},
                        chatType: CHAT_TYPE_SINGLE,
                        chatId: userId,
                        chatJid: data?.userJid,
                    };
                    return dispatch(ActiveChatAction(activeChatData));
                }
            }
        },
        [dispatch, recentChatData, vCardData.data.userId],
    );

    const isTranslated = () =>
        !isSender &&
        pageType === 'conversation' &&
        msgBody?.translatedMessage &&
        Object.keys(msgBody.translatedMessage).length;

    const renderMessageBody = (inputData, mentionedLst) => {
        let mentionedUsers;
        // Use original message if inputData is not provided, then convert to URL
        const originalText = inputData || msgBody.message;

        // First, check if the message already contains HTML anchor tags
        // If it does, we need to clean it up before processing
        let cleanedOriginalText = originalText;
        if (originalText && typeof originalText === 'string') {
            // Remove duplicate URLs that appear after anchor tags
            // Pattern: <a href="url">url</a>url -> <a href="url">url</a>
            cleanedOriginalText = originalText.replace(
                /(<a\s+[^>]*href=["']([^"']+)["'][^>]*>)([^<]*?)(<\/a>)\2/gi,
                '$1$3$4',
            );

            // Also handle cases where the URL appears after closing tag without quotes
            cleanedOriginalText = cleanedOriginalText.replace(/(<\/a>)(https?:\/\/[^\s<>"']+)/gi, '$1');

            // Handle www URLs that might be duplicated after anchor tags
            cleanedOriginalText = cleanedOriginalText.replace(/(<\/a>)(www\.[^\s<>"']+)/gi, '$1');
        }

        const textData = inputData
            ? inputData
            : cleanedOriginalText
                ? convertTextToURL(cleanedOriginalText)
                : messageLink;

        if (msgBody && msgBody.mentionedUsersIds) {
            mentionedUsers = mentionedLst ? mentionedLst : msgBody.mentionedUsersIds;
        } else {
            mentionedUsers = [];
        }

        const rawText = getFormattedText(textData);

        const containsMention = /@\[\?\]/.test(rawText);
        const containsAngleBrackets = /</.test(rawText);

        // Check if the angle brackets are from valid anchor tags (from linkify)
        const hasValidAnchorTags = /<a\s+[^>]*href=["'][^"']+["'][^>]*>.*?<\/a>/i.test(rawText);
        const hasValidFormattingTags = /<\/?(b|i|u)>/i.test(rawText);

        const processedText = handleMentionedUser({
            text: rawText,
            mentionedUsersIds: mentionedUsers,
            mentionedMe: !isSender && data.userId,
            mentionedClass: null,
            chatType: messageObject.chatType,
        });

        // Only escape HTML if it has angle brackets, is NOT a mention, and does NOT have valid anchor tags
        // Valid anchor tags from linkify should be preserved, not escaped
        let safeText =
            containsAngleBrackets && !containsMention && !hasValidAnchorTags && !hasValidFormattingTags ? escapeHtml(rawText) : processedText;

        if (hasValidAnchorTags) {
            safeText = safeText.replace(/(<\/a>)\s*(https?:\/\/|www\.)[^\s<>"']+/gi, '$1');
            safeText = safeText.replace(/(<a\s+[^>]*href=["']([^"']+)["'][^>]*>)([^<]*?)(<\/a>)\s*\2/gi, '$1$3$4');
        }

        const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic}|:([a-zA-Z_]+):)/gu;

        let formattedText = safeText.replace(emojiRegex, match => {
            if (match.startsWith(':') && match.endsWith(':')) {
                const inner = match.slice(1, -1);
                if (/^\d{1,2}$/.test(inner)) {
                    return match;
                }
            }
            return `<span class="message-emoji">${match}</span>`;
        });

        // return msgBody ? htmlToReactParser.parse(formattedText) : null;
        return msgBody ? <span dangerouslySetInnerHTML={{ __html: formattedText }} /> : null;
    };

    const subscribeToCall = async meetLink => {
        if (blockOfflineAction()) return '';
        const callLink = meetLink?.split(`${getSiteDomain()}/`)[1];
        if (
            callData &&
            (conferenceData.callStatusText === CALL_STATUS_CONNECTED ||
                conferenceData.callStatusText === CALL_STATUS_RECONNECT)
        ) {
            const roomLink = await SDK.getCallLink();
            if (meetLink.includes(roomLink.data)) {
                props.handleShowCallScreen();
            } else {
                Store.dispatch(
                    showModal({
                        open: true,
                        modelType: 'CallConfirm',
                        newCallLink: callLink,
                    }),
                );
            }
        } else {
            Store.dispatch(callIntermediateScreen({ show: true, link: callLink }));
        }
    };

    useEffect(() => {
        const urlRegex = /https?:\/\/\S+/g;
        const ArrayOfLinkSplit = (msgBody.message && msgBody.message?.split(urlRegex)) || [];
        const meetLinkUrl = (msgBody.message && msgBody.message?.match(urlRegex)) || [];

        const prevMeetTextMentionsLgn = ArrayOfLinkSplit[0]?.match(/@\[\?]/g)?.length;
        const prevMeetTextMentionsFLgn = prevMeetTextMentionsLgn ? prevMeetTextMentionsLgn : 0;
        const endMeetTextMentionsLgn = ArrayOfLinkSplit[1]?.match(/@\[\?]/g)?.length;
        const prevMeetTextMentionsLst =
            msgBody.mentionedUsersIds && msgBody.mentionedUsersIds.slice(0, prevMeetTextMentionsFLgn);
        const endMeetTextMentionsLst =
            msgBody.mentionedUsersIds &&
            msgBody.mentionedUsersIds.slice(
                prevMeetTextMentionsFLgn,
                prevMeetTextMentionsFLgn + endMeetTextMentionsLgn,
            );

        // Fix: Only show Card View if there is EXACTLY one meeting link.
        // If multiple links exist, fallback to standard text view (meetLink: null).
        if (
            meetLinkUrl.length === 1 &&
            ((meetLinkUrl[0] && isCallLink(meetLinkUrl[0])) || (editedStatus === 1 && meetLinkUrl[0]))
        ) {
            setCallLinkMessageBody({
                prevMeetText: ArrayOfLinkSplit[0],
                meetLink: meetLinkUrl[0],
                endMeetText: ArrayOfLinkSplit[1],
                prevtextMentionuser: prevMeetTextMentionsLst,
                endtextMentionuser: endMeetTextMentionsLst,
                editedMeetLink: meetLinkUrl[0],
            });
        } else {
            setCallLinkMessageBody({
                prevtextMentionuser: prevMeetTextMentionsLst,
                endtextMentionuser: endMeetTextMentionsLst,
                prevMeetText: ArrayOfLinkSplit[0],
                meetLink: null,
                endMeetText: ArrayOfLinkSplit[1],
                editedMeetLink: null,
            });
        }
    }, [editedStatus, msgBody.mentionedUsersIds, msgBody.message]);

    const handleGlobalClick = React.useCallback(
        e => {
            // First handle mentioned user clicks
            handleMentionedUserClick(e);

            // Check for anchor tags (with or without 'link' class)
            const anchor = e.target.closest('a');
            if (!anchor) return;

            const href = anchor.getAttribute('href');
            if (!href) return;

            // Check if it's a channel invite link
            if (MFChannel.isValidChannelURL(href)) {
                e.preventDefault();
                e.stopPropagation();
                setActionModal({
                    visible: true,
                    body: <JoinRequest accessUri={href} onClose={resetActionModal} />,
                    onRequestClose: resetActionModal,
                });
                return;
            }

            // Check if it's a group invite link
            if (isGroupInviteLink(href)) {
                e.preventDefault();
                e.stopPropagation();
                setActionModal({
                    visible: true,
                    body: <GroupJoinRequest accessUri={href} onClose={resetActionModal} />,
                    onRequestClose: resetActionModal,
                });
                return;
            }
        },
        [handleMentionedUserClick],
    );

    useEffect(() => {
        document.addEventListener('click', handleGlobalClick, { passive: false });
        return () => {
            document.removeEventListener('click', handleGlobalClick);
        };
    }, [handleGlobalClick]);

    const isEditedLink = editedStatus === 1 && callLinkMessageBody.editedMeetLink?.length > 1;
    const isValidMeetLink = isEditedLink
        ? isCallLink(callLinkMessageBody?.editedMeetLink)
        : isCallLink(callLinkMessageBody.meetLink);
    const isMeetingDomainLink = link => typeof link === 'string' && link.startsWith(REACT_APP_SITE_DOMAIN);
    return (
        <div className="meetinglink">
            <Fragment>
                {callLinkMessageBody.meetLink &&
                    isMeetingDomainLink(callLinkMessageBody.meetLink) &&
                    isValidMeetLink ? (
                    <div className="message_meeting_link">
                        <button
                            type="button"
                            className="meetingL_link_share"
                            onClick={() => subscribeToCall(callLinkMessageBody.meetLink)}
                        >
                            <div className="meeting_link_detail">
                                <div className="meetinglink_header">
                                    <div>
                                        <span className="meeting_ink">{callLinkMessageBody.meetLink}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="meeting_bg">
                                <div className="meetinglink_user_message">
                                    You have been invited to join the EN Connect meeting. Follow the link below to join the
                                    call
                                    <div>
                                        <img
                                            className="mirrorfly_meeting_logo"
                                            src={MeetingLinkIcon}
                                            alt="EN Connect Video Call"
                                        />
                                    </div>
                                </div>
                                <div style={{ padding: '0px 5px' }} ref={messageRef}>
                                    <span className="textmessages">
                                        {callLinkMessageBody.prevMeetText &&
                                            renderMessageBody(
                                                callLinkMessageBody.prevMeetText,
                                                callLinkMessageBody.prevtextMentionuser,
                                            )}
                                    </span>

                                    <span className="textmessages">
                                        {callLinkMessageBody.endMeetText &&
                                            renderMessageBody(
                                                callLinkMessageBody.endMeetText,
                                                callLinkMessageBody.endtextMentionuser,
                                            )}
                                    </span>
                                </div>
                                <div className="meeting_desc">
                                    <span className="call_details copiedLink">Join Video Call</span>
                                </div>
                            </div>
                            {!!isTranslated() && <Translate tMessage={msgBody?.translatedMessage} />}
                        </button>
                    </div>
                ) : (
                    <div className={`message-text ${editedStatus === 1 ? 'messageedited' : ''}`} ref={messageRef}>
                        <span>{renderMessageBody()}</span>
                        {!!isTranslated() && <Translate tMessage={msgBody?.translatedMessage} />}
                    </div>
                )}
            </Fragment>
        </div>
    );
}

TextComponent.propTypes = {
    messageObject: PropTypes.shape({
        msgBody: PropTypes.shape({
            message: PropTypes.string,
            mentionedUsersIds: PropTypes.array,
            media: PropTypes.shape({
                fileName: PropTypes.string,
                file_size: PropTypes.number,
                file_url: PropTypes.string,
                file_key: PropTypes.string,
            }),
        }),
        editedStatus: PropTypes.number,
    }),
    isSender: PropTypes.bool,
    pageType: PropTypes.string,
    handleShowCallScreen: PropTypes.func,
};
